import React, { useState } from 'react';
import { Phone, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const QuickTestCallCard = ({ onPlaceCall, lastCallStatus, isPollingStatus, placingCall }) => {
  const [testCallName, setTestCallName] = useState('');
  const [testCallPhone, setTestCallPhone] = useState('');

  const handleClick = async () => {
    const success = await onPlaceCall({ name: testCallName, phone: testCallPhone });
    if (success) {
      setTestCallName('');
      setTestCallPhone('');
    }
  };

  return (
    <Card className="glass-card border-white/20">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-primary" />
          <div>
            <CardTitle>Quick Test Call</CardTitle>
            <CardDescription className="mt-1">Place a test call instantly without creating leads</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="test-call-name">Customer Name</Label>
              <Input
                id="test-call-name"
                placeholder="e.g., Money"
                value={testCallName}
                onChange={(e) => setTestCallName(e.target.value)}
                disabled={placingCall}
                className="bg-secondary/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-call-phone">Phone Number</Label>
              <Input
                id="test-call-phone"
                placeholder="e.g., +14039780703"
                value={testCallPhone}
                onChange={(e) => setTestCallPhone(e.target.value)}
                disabled={placingCall}
                className="bg-secondary/50"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={handleClick}
              disabled={placingCall || !testCallName.trim() || !testCallPhone.trim()}
              className="gap-2"
              data-testid="place-test-call-btn"
            >
              <Phone className={`w-4 h-4 ${placingCall ? 'animate-pulse' : ''}`} />
              {placingCall ? 'Placing Call...' : 'Place Test Call'}
            </Button>

            {placingCall && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span>Connecting to Retell...</span>
              </div>
            )}
          </div>

          {/* Last Call Status Badge */}
          {lastCallStatus && (
            <div className="mt-3 p-3 rounded-lg bg-secondary/30 border border-white/10" data-testid="last-call-status">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground">Last Call:</span>
                  <Badge
                    variant={
                      lastCallStatus.status === 'completed' ? 'default' :
                      lastCallStatus.status === 'in-progress' || lastCallStatus.status === 'connected' ? 'default' :
                      lastCallStatus.status === 'ringing' || lastCallStatus.status === 'initiated' ? 'secondary' :
                      lastCallStatus.status === 'failed' || lastCallStatus.status === 'no-answer' ? 'destructive' :
                      'outline'
                    }
                    className={`
                      ${lastCallStatus.status === 'completed' ? 'bg-green-500/20 text-green-400 border-green-500/30' : ''}
                      ${lastCallStatus.status === 'in-progress' || lastCallStatus.status === 'connected' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30 animate-pulse' : ''}
                      ${lastCallStatus.status === 'ringing' || lastCallStatus.status === 'initiated' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30 animate-pulse' : ''}
                      ${lastCallStatus.status === 'failed' || lastCallStatus.status === 'no-answer' || lastCallStatus.status === 'busy' ? 'bg-red-500/20 text-red-400 border-red-500/30' : ''}
                      ${lastCallStatus.status === 'cancelled' ? 'bg-gray-500/20 text-gray-400 border-gray-500/30' : ''}
                    `}
                  >
                    {lastCallStatus.status === 'completed' && '✓ '}
                    {lastCallStatus.status === 'failed' && '✗ '}
                    {lastCallStatus.status?.replace('-', ' ').toUpperCase() || 'UNKNOWN'}
                  </Badge>
                  {isPollingStatus && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-primary"></div>
                      <span>updating...</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {lastCallStatus.phone && <span className="mr-2">{lastCallStatus.phone}</span>}
                  {lastCallStatus.duration && <span className="mr-2">{Math.round(lastCallStatus.duration / 1000)}s</span>}
                  {lastCallStatus.timestamp && (
                    <span>{new Date(lastCallStatus.timestamp).toLocaleTimeString()}</span>
                  )}
                </div>
              </div>
              {lastCallStatus.error && (
                <p className="mt-2 text-xs text-red-400">{lastCallStatus.error}</p>
              )}
            </div>
          )}

          <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-xs text-muted-foreground">
                <p className="font-semibold mb-1">Note:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Call will use the first available campaign</li>
                  <li>A lead will be created automatically</li>
                  <li>View call status in Dashboard or Call History</li>
                  <li>After call completes, click 'Resync Calls' to update status</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default QuickTestCallCard;
